import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checkbox";
import { ArrowLeft, User, Mail, Lock, Phone, Wallet, Target } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";

const InvestorRegister = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",

    // Investment Information
    investmentCapacity: "",
    preferredSector: "", // Changed from array to single string

    investmentExperience: "",

    // Additional Details
    occupation: "",
    companyName: "",
    linkedin: "",
  });

  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { registerInvestor } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!formData.fullName || !formData.email || !formData.investmentCapacity) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const result = await registerInvestor({
        name: formData.fullName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        company: formData.companyName,
        bio: formData.investmentExperience, // using bio field to store investment experience
        location: undefined, // not using location in this form
        investmentCapacity: formData.investmentCapacity ? parseFloat(formData.investmentCapacity) : undefined,
        preferredSectors: formData.preferredSector ? [formData.preferredSector] : undefined, // Convert to array for backend compatibility
        investmentExperience: formData.investmentExperience,
        occupation: formData.occupation,
        linkedin: formData.linkedin,
      });

      if (result) {
        toast({
          title: "Registration Submitted",
          description: "Your application has been submitted for review. We'll notify you once approved.",
        });

        // Navigate to investor waiting approval page
        navigate("/waiting-approval/investor");
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : "Failed to submit registration",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t("common.backToHome")}
          </Link>

          <Card className="border-border shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {t("auth.investor.register.title")}
              </CardTitle>
              <CardDescription>
                {t("auth.investor.register.subtitle")}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {t("auth.investor.register.personalInfo")}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">{t("auth.investor.register.fullName")}</Label>
                      <div className="relative">
                        <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          placeholder={t("auth.investor.register.fullName")}
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <User className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{t("auth.investor.register.email")}</Label>
                      <div className="relative">
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="example@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Mail className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">{t("auth.investor.register.phone")}</Label>
                      <div className="relative">
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder={t("auth.investor.register.phone")}
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Phone className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="occupation">{t("auth.investor.register.occupation")}</Label>
                      <Input
                        id="occupation"
                        name="occupation"
                        type="text"
                        placeholder={t("auth.investor.register.occupation")}
                        value={formData.occupation}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {t("auth.investor.register.accountSecurity")}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">{t("auth.investor.register.password")}</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder={t("auth.investor.register.password")}
                          value={formData.password}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">{t("auth.investor.register.confirmPassword")}</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          placeholder={t("auth.investor.register.confirmPassword")}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Investment Information Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    {t("auth.investor.register.investmentInfo")}
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="investmentCapacity">{t("auth.investor.register.investmentCapacity")}</Label>
                      <div className="relative">
                        <Input
                          id="investmentCapacity"
                          name="investmentCapacity"
                          type="number"
                          placeholder={t("auth.investor.register.investmentCapacity")}
                          value={formData.investmentCapacity}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Wallet className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t("auth.investor.register.investmentCapacity")}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferredSector">{t("auth.investor.register.preferredSectors")}</Label>
                      <div className="relative">
                        <Input
                          id="preferredSector"
                          name="preferredSector"
                          type="text"
                          placeholder={t("auth.investor.register.preferredSectors")}
                          value={formData.preferredSector}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Target className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t("auth.investor.register.preferredSectors")}
                      </p>
                    </div>



                    <div className="space-y-2">
                      <Label htmlFor="investmentExperience">{t("auth.investor.register.investmentExperience")}</Label>
                      <Textarea
                        id="investmentExperience"
                        name="investmentExperience"
                        placeholder={t("auth.investor.register.investmentExperience")}
                        value={formData.investmentExperience}
                        onChange={handleInputChange}
                        className="min-h-[100px]"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="pb-6">
                  <h3 className="text-lg font-semibold mb-4">{t("auth.investor.register.additionalInfo")}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">{t("auth.investor.register.companyNameOptional")}</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        placeholder={t("auth.investor.register.companyNameOptional")}
                        value={formData.companyName}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="linkedin">{t("auth.investor.register.linkedin")}</Label>
                      <Input
                        id="linkedin"
                        name="linkedin"
                        type="url"
                        placeholder="https://linkedin.com/in/yourprofile"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                    disabled={loading}
                  >
                    {loading ? t("common.loading") : t("auth.investor.register.submit")}
                  </Button>

                  <Button type="button" variant="outline" asChild className="w-full sm:w-auto">
                    <Link to="/register">
                      {t("auth.investor.register.back")}
                    </Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InvestorRegister;